import React, { useEffect, useMemo, useState } from "react";
import { timeLogsAPI } from "../../api/client";
import { formatDurationSeconds } from "../../utils/formatDuration";
import "./TimerWidget.css";

const STATES = {
  NOT_STARTED: "NOT_STARTED",
  RUNNING: "RUNNING",
  PAUSED: "PAUSED",
  BLOCKED: "BLOCKED",
};

const STATUS_CONFIG = {
  [STATES.NOT_STARTED]: { label: "Not started", className: "timer-status-badge--idle" },
  [STATES.RUNNING]: { label: "Running", className: "timer-status-badge--running" },
  [STATES.PAUSED]: { label: "Paused", className: "timer-status-badge--paused" },
  [STATES.BLOCKED]: { label: "Blocked", className: "timer-status-badge--blocked" },
};

function toBaseSeconds(totalTimeMinutes) {
  return Math.max(0, Math.round(totalTimeMinutes * 60));
}

export default function TimerWidget({
  stageAssignmentId,
  totalTimeMinutes = 0,
  onStateChange,
  isLocked = false,
}) {
  const [state, setState] = useState(STATES.NOT_STARTED);
  const [startedAt, setStartedAt] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(() =>
    toBaseSeconds(totalTimeMinutes),
  );
  const [error, setError] = useState("");

  const baseSeconds = useMemo(
    () => toBaseSeconds(totalTimeMinutes),
    [totalTimeMinutes],
  );
  const sessionSeconds = Math.max(0, elapsedSeconds - baseSeconds);

  useEffect(() => {
    setElapsedSeconds(baseSeconds);
  }, [baseSeconds]);

  useEffect(() => {
    if (isLocked) {
      setStartedAt(null);
      if (totalTimeMinutes > 0) {
        setState(STATES.PAUSED);
      } else {
        setState(STATES.NOT_STARTED);
      }
      return;
    }

    const load = async () => {
      try {
        const resp = await timeLogsAPI.active();
        const active = resp.data;
        if (
          active?.stageAssignmentId &&
          active.stageAssignmentId !== stageAssignmentId
        ) {
          setState(STATES.BLOCKED);
          return;
        }
        if (active?.stageAssignmentId === stageAssignmentId) {
          setState(STATES.RUNNING);
          setStartedAt(new Date(active.startedAt));
          return;
        }
        if (totalTimeMinutes > 0) {
          setState(STATES.PAUSED);
        } else {
          setState(STATES.NOT_STARTED);
        }
      } catch (err) {
        setError("Failed to load timer");
      }
    };
    load();
  }, [isLocked, stageAssignmentId, totalTimeMinutes]);

  useEffect(() => {
    if (state !== STATES.RUNNING || !startedAt) return;
    const interval = setInterval(() => {
      const now = new Date();
      const diffSeconds = Math.max(
        0,
        Math.round((now.getTime() - startedAt.getTime()) / 1000),
      );
      setElapsedSeconds(baseSeconds + diffSeconds);
    }, 1000);
    return () => clearInterval(interval);
  }, [state, startedAt, baseSeconds]);

  useEffect(() => {
    if (onStateChange) onStateChange(state);
  }, [state, onStateChange]);

  const start = async () => {
    setError("");
    try {
      const resp = await timeLogsAPI.start(stageAssignmentId);
      setStartedAt(new Date(resp.data.startedAt));
      setState(STATES.RUNNING);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to start timer");
    }
  };

  const pause = async () => {
    setError("");
    try {
      await timeLogsAPI.pause(stageAssignmentId);
      setState(STATES.PAUSED);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to pause timer");
    }
  };

  const resume = async () => {
    setError("");
    try {
      const resp = await timeLogsAPI.resume(stageAssignmentId);
      setStartedAt(new Date(resp.data.startedAt));
      setState(STATES.RUNNING);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to resume timer");
    }
  };

  const statusConfig = STATUS_CONFIG[state] || STATUS_CONFIG[STATES.NOT_STARTED];

  // One shell for every state: the header and the readout never move, only the
  // action area below them changes. Earlier each state built its own layout, so
  // the widget jumped around as the timer went from idle to running to paused.
  const shell = (modifier, statusNode, children) => (
    <div className={`panel timer-widget timer-widget--${modifier}`}>
      <div className="timer-widget-header">
        <span className="timer-widget-label">Time Tracking</span>
        {statusNode}
      </div>
      {children}
      {error && <div className="timer-widget-error">{error}</div>}
    </div>
  );

  const statusBadge = (
    <span className={`timer-status-badge ${statusConfig.className}`}>
      {state === STATES.RUNNING && <span className="timer-status-dot" />}
      {statusConfig.label}
    </span>
  );

  const readout = (
    <div className="timer-widget-readout">
      <div className="timer-widget-total">
        {formatDurationSeconds(elapsedSeconds)}
      </div>
      <div className="timer-widget-total-label">Total time logged</div>
    </div>
  );

  if (isLocked) {
    return shell(
      "locked",
      <span className="timer-status-badge timer-status-badge--idle">
        Closed
      </span>,
      <>
        {readout}
        <p className="timer-widget-hint">
          This stage is submitted or completed — the timer is closed.
        </p>
      </>,
    );
  }

  if (state === STATES.BLOCKED) {
    return shell(
      "blocked",
      statusBadge,
      <div className="timer-widget-notice">
        <strong>You already have a timer running.</strong>
        <span>Finish or pause the other task before starting this one.</span>
      </div>,
    );
  }

  return shell(
    state.toLowerCase().replace("_", "-"),
    statusBadge,
    <>
      {readout}

      {state === STATES.RUNNING && (
        <div className="timer-widget-session">
          <span className="timer-widget-session-value">
            +{formatDurationSeconds(sessionSeconds)}
          </span>
          <span className="timer-widget-session-label">
            this session
            {startedAt &&
              ` · since ${startedAt.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}`}
          </span>
        </div>
      )}

      {state === STATES.NOT_STARTED && (
        <>
          <button
            className="timer-widget-btn timer-widget-btn--start"
            onClick={start}
          >
            Start Working
          </button>
          <p className="timer-widget-hint">
            Start the timer before you begin — you can pause anytime and come
            back later.
          </p>
        </>
      )}

      {state === STATES.RUNNING && (
        <button
          className="timer-widget-btn timer-widget-btn--pause"
          onClick={pause}
        >
          Take a Break
        </button>
      )}

      {state === STATES.PAUSED && (
        <>
          <button
            className="timer-widget-btn timer-widget-btn--resume"
            onClick={resume}
          >
            Resume Working
          </button>
          <p className="timer-widget-hint">
            Paused — time stops counting until you resume.
          </p>
        </>
      )}
    </>,
  );
}

export { STATES as TIMER_STATES };
